import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  EParcelStoreKind,
  IMailroomSettings,
  IParcelLookup,
  IParcelStore,
  IRegistryOffice,
} from "dto/Mailroom/IMailroom";
import {
  IIncomingRegisterRequest,
  IIncomingRegisterResponse,
  IInternalRegisterRequest,
  IInternalRegisterResponse,
  IOutgoingRegisterRequest,
  IOutgoingRegisterResponse,
} from "dto/Mailroom/IMailroomRegister";

// Serwis biura podawczego: słowniki (rodzaje załączników, formy doręczenia),
// biura podawcze, składy oraz ustawienia urzędu (domyślny dekretujący).
// Kreator bierze słowniki z /incomingParcel/init; te hooki zasilają panel admina.

export const mailroomQueryKeys = {
  attachmentTypes: "mailroom.attachmentTypes",
  deliveryKinds: "mailroom.deliveryKinds",
  registryOffices: "mailroom.registryOffices",
  parcelStores: "mailroom.parcelStores",
  settings: "mailroom.settings",
};

export interface ISaveLookupRequest {
  Id?: number | null;
  Name: string;
  SortOrder: number;
  /** Opłata pocztowa (cennik listów) — tylko formy doręczenia. */
  Fee?: number | null;
}

export interface ISaveRegistryOfficeRequest {
  Id?: string | null;
  Name: string;
  Symbol?: string | null;
  IsDefault: boolean;
}

export interface ISaveParcelStoreRequest {
  Id?: string | null;
  Name: string;
  NumberFormat: string;
  Kind: EParcelStoreKind;
  IsElectronic: boolean;
  ContinuousForYear: boolean;
  ContinuousForUser: boolean;
  IsDefault: boolean;
  IsActive: boolean;
}

export interface IUpdateMailroomSettingsRequest {
  DefaultReceiverUserId?: string | null;
  DefaultRegistryOfficeId?: string | null;
  DefaultParcelStoreId?: string | null;
}

// ── Słowniki ──────────────────────────────────────────────────────────────

const getAttachmentTypes = (): Promise<{ data: IParcelLookup[] }> =>
  axiosInstance.get("mailroom/attachmentTypes");

const useGetAttachmentTypes = () =>
  useCustomQuery(mailroomQueryKeys.attachmentTypes, () => getAttachmentTypes());

const getDeliveryKinds = (): Promise<{ data: IParcelLookup[] }> =>
  axiosInstance.get("mailroom/deliveryKinds");

const useGetDeliveryKinds = () =>
  useCustomQuery(mailroomQueryKeys.deliveryKinds, () => getDeliveryKinds());

const saveAttachmentType = (
  payload: ISaveLookupRequest,
): Promise<{ data: IParcelLookup }> =>
  axiosInstance.post("mailroom/attachmentTypes", payload);

const useSaveAttachmentType = () => useCustomMutation(saveAttachmentType);

const saveDeliveryKind = (
  payload: ISaveLookupRequest,
): Promise<{ data: IParcelLookup }> =>
  axiosInstance.post("mailroom/deliveryKinds", payload);

const useSaveDeliveryKind = () => useCustomMutation(saveDeliveryKind);

const removeAttachmentType = (id: number): Promise<{ data: unknown }> =>
  axiosInstance.delete(`mailroom/attachmentTypes/${id}`);

const useRemoveAttachmentType = () => useCustomMutation(removeAttachmentType);

const removeDeliveryKind = (id: number): Promise<{ data: unknown }> =>
  axiosInstance.delete(`mailroom/deliveryKinds/${id}`);

const useRemoveDeliveryKind = () => useCustomMutation(removeDeliveryKind);

// ── Biura podawcze / składy ────────────────────────────────────────────────

const getRegistryOffices = (): Promise<{ data: IRegistryOffice[] }> =>
  axiosInstance.get("mailroom/registryOffices");

const useGetRegistryOffices = () =>
  useCustomQuery(mailroomQueryKeys.registryOffices, () => getRegistryOffices());

const saveRegistryOffice = (
  payload: ISaveRegistryOfficeRequest,
): Promise<{ data: IRegistryOffice }> =>
  axiosInstance.post("mailroom/registryOffices", payload);

const useSaveRegistryOffice = () => useCustomMutation(saveRegistryOffice);

const removeRegistryOffice = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`mailroom/registryOffices/${id}`);

const useRemoveRegistryOffice = () => useCustomMutation(removeRegistryOffice);

const getParcelStores = (): Promise<{ data: IParcelStore[] }> =>
  axiosInstance.get("mailroom/parcelStores");

const useGetParcelStores = () =>
  useCustomQuery(mailroomQueryKeys.parcelStores, () => getParcelStores());

const saveParcelStore = (
  payload: ISaveParcelStoreRequest,
): Promise<{ data: IParcelStore }> =>
  axiosInstance.post("mailroom/parcelStores", payload);

const useSaveParcelStore = () => useCustomMutation(saveParcelStore);

const removeParcelStore = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`mailroom/parcelStores/${id}`);

const useRemoveParcelStore = () => useCustomMutation(removeParcelStore);

// ── Ustawienia urzędu ──────────────────────────────────────────────────────

const getSettings = (): Promise<{ data: IMailroomSettings }> =>
  axiosInstance.get("mailroom/settings");

const useGetSettings = () =>
  useCustomQuery(mailroomQueryKeys.settings, () => getSettings());

const updateSettings = (
  payload: IUpdateMailroomSettingsRequest,
): Promise<{ data: IMailroomSettings }> =>
  axiosInstance.put("mailroom/settings", payload);

const useUpdateSettings = () => useCustomMutation(updateSettings);

// ── Rejestry biura (port „Rejestry" z Magistratu) ─────────────────────────
// POST, bo filtr ma listy (lata/miesiące). Take=0 → cały wynik (wydruk listy).

const getIncomingRegister = (
  request: IIncomingRegisterRequest,
): Promise<{ data: IIncomingRegisterResponse }> =>
  axiosInstance.post("mailroom/register/incoming", request);

const useGetIncomingRegister = (
  request: IIncomingRegisterRequest,
  enabled = true,
) =>
  useCustomQuery(
    ["mailroom.register.incoming", request],
    () => getIncomingRegister(request),
    { enabled, keepPreviousData: true },
  );

const getOutgoingRegister = (
  request: IOutgoingRegisterRequest,
): Promise<{ data: IOutgoingRegisterResponse }> =>
  axiosInstance.post("mailroom/register/outgoing", request);

const useGetOutgoingRegister = (
  request: IOutgoingRegisterRequest,
  enabled = true,
) =>
  useCustomQuery(
    ["mailroom.register.outgoing", request],
    () => getOutgoingRegister(request),
    { enabled, keepPreviousData: true },
  );

const getInternalRegister = (
  request: IInternalRegisterRequest,
): Promise<{ data: IInternalRegisterResponse }> =>
  axiosInstance.post("mailroom/register/internal", request);

const useGetInternalRegister = (
  request: IInternalRegisterRequest,
  enabled = true,
) =>
  useCustomQuery(
    ["mailroom.register.internal", request],
    () => getInternalRegister(request),
    { enabled, keepPreviousData: true },
  );

export default {
  useGetAttachmentTypes,
  useGetDeliveryKinds,
  useSaveAttachmentType,
  useSaveDeliveryKind,
  useRemoveAttachmentType,
  useRemoveDeliveryKind,
  useGetRegistryOffices,
  useSaveRegistryOffice,
  useRemoveRegistryOffice,
  useGetParcelStores,
  useSaveParcelStore,
  useRemoveParcelStore,
  useGetSettings,
  useUpdateSettings,
  getIncomingRegister,
  useGetIncomingRegister,
  getOutgoingRegister,
  useGetOutgoingRegister,
  getInternalRegister,
  useGetInternalRegister,
};
